import React from 'react';
import PropTypes from 'prop-types';
import cname from '../../style.module.scss';
import {ZeroButton, PowerButton} from '../../SpectrophotometerImages';
import {throttle} from '@learningblocks/exp-constants/lib/Utilities';

const THROTTLE_DELAY = 250;

export function Controller(props) {
  const [value, setValue] = React.useState(props.wavelength);

  React.useEffect(() => {
    if (value !== props.wavelength) {
      setValue(props.wavelength);
    }
  }, [props.wavelength]);

  const renderAbsorbance = () => {
    let absorbance = props.absorbanceValue;
    if (props.absorbanceValue) {
      absorbance = props.absorbanceValue - props.relativeAbsorbance;
    }
    return absorbance.toFixed(3);
  };

  const onClickZeroButton = () =>
    props.onClickZeroButton(props.absorbanceValue);

  const onClickPowerButton = () => props.onClickPowerButton(!props.poweredUp);

  const onInputChange = (event) => {
    if (props.poweredUp) {
      let newValue = event.target.valueAsNumber;
      setValue(newValue);
      throttle(() => {
        props.setWavelength(newValue);
      }, THROTTLE_DELAY);
    }
  };

  const setDisplayClassName = () => {
    if (props.poweredUp) return `${cname.displayBlock} ${cname.poweredUp}`;
    else return `${cname.displayBlock}`;
  };

  const setDisplayAltText = () => {
    return props.poweredUp ? `Spectrophotometer display. Wavelength: ${value} nanometers. Absorbance: ${renderAbsorbance()}.` :
      `Spectrophotometer display. Powered off`
  }

  return (
    <div className={cname.controller}>
      <div className={setDisplayClassName()} ariaLabel={setDisplayAltText()}>
        {props.poweredUp && (
          <>
            <p className={cname.wavelength}> WAVELENGTH: {value}nm</p>
            <p className={cname.absorbanceTitle}>ABSORBANCE</p>
            <p className={cname.absorbanceValue}>{renderAbsorbance()}</p>
          </>
        )}
      </div>
      <div className={cname.waveBlock}>
        <p className={cname.title}>Wavelength (nm)</p>
        <div className={cname.waveMeter} ariaLabel={`Wavelength slider which runs from 350 nanometers to 750 nanometers. Use the [left and right] arrow keys to adjust the wavelength.`}>
          <div className={cname.scale}>
            <span>350</span>
            <span>400</span>
            <span>450</span>
            <span>500</span>
            <span>550</span>
            <span>600</span>
            <span>650</span>
            <span>700</span>
            <span>750</span>
          </div>
          <input
            type='range'
            min='350'
            max='750'
            value={value}
            step='5'
            onChange={onInputChange}
          />
        </div>
      </div>
      <div className={cname.buttonBlock}>
        <ZeroButton className={cname.zerobutton} onClick={onClickZeroButton} />
        <PowerButton
          className={cname.powerbutton}
          onClick={onClickPowerButton}
        />
      </div>
    </div>
  );
}

Controller.propTypes = {
  /**
   * Absorbance value of solution
   */
  absorbanceValue: PropTypes.number,
  /**
   * Relative absorbance value set
   */
  relativeAbsorbance: PropTypes.number,
  /**
   * Callback for PowerButton click
   */
  onClickPowerButton: PropTypes.func,
  /**
   * Callback for ZeroButton click
   */
  onClickZeroButton: PropTypes.func,
  /**
   * Current wavelength
   */
  wavelength: PropTypes.number,
  /**
   * Callback for wavelength slider change
   */
  setWavelength: PropTypes.func,
  /**
   * Boolean to set if spectrophotometer is on/off
   */
  poweredUp: PropTypes.bool,
};

Controller.defaultProps = {
  absorbanceValue: 0,
  relativeAbsorbance: 0,
};
